import { condicionesPruebaTarjeta } from './pruebaConTarjeta';
// La invitación de la campaña del 25 de octubre, dentro de la app: el aviso
// del inicio y la pantalla "Lo nuevo de MiPAE" a la que lleva.
//
// Ajuste de enfoque de Sebastián, 20-oct-2026: el aviso invita a conocer lo
// nuevo, usar las herramientas y probar las membresías. Lo primero es una
// utilidad concreta de la experiencia de la persona, con una muestra real de
// la herramienta y una acción clara, y no una tabla de condiciones. La
// secuencia es siempre la misma:
//   1. Qué puedes hacer con MiPAE (lo que ya es gratis, con su límite).
//   2. Qué te aporta la membresía (solo lo que de verdad suma).
//   3. La invitación a conocerla o probarla.
//   4. El acceso a las condiciones completas.
//
// Tres reglas que este archivo sostiene y sus pruebas fijan:
//   - Nada que también es gratis se atribuye a la membresía. Las dos listas
//     salen de las mismas constantes que aplican el acceso (CUOTA_POR_ROL,
//     CUOTA_PLUS, CUOTA_EVOLUCION_SEMANA_PLUS, MAX_PROPIOS_*), nunca de un
//     número escrito a mano.
//   - Abrir el aviso jamás inicia la prueba. La prueba se ofrece con un botón
//     explícito ("Probar 3 días gratis") y solo cuando corresponde: cuando
//     suma algo que la persona no tiene hoy. A una enfermera, mientras dure
//     el acceso abierto de octubre, una prueba le gastaría días que ya tiene
//     cubiertos; lo mismo a una estudiante que conserva sus condiciones hasta
//     el 24. A ellas se les dice desde cuándo.
//   - El mensaje se adapta a lo que la persona ya tiene: con una membresía
//     vigente no se vende nada, con la prueba en curso se dice hasta cuándo, y
//     con la prueba usada no se vuelve a ofrecer.
//
// Puro y sin red. La venta (precio, suscribirse) la decide la pantalla con
// `nativo` y `contratable`: en la app instalada no existe (venta-solo-web).
import {
  CUOTA_FREE, CUOTA_POR_ROL, CUOTA_PLUS, CUOTA_EVOLUCION_SEMANA, CUOTA_EVOLUCION_SEMANA_PLUS,
  ESTUDIANTE_FIN_TRANSICION, enTrial, finTrial, trialDisponible, tieneAccesoPlus, enTransicionEstudiante,
  diasTrialPara,
} from './planGating';
import { PRODUCTOS_PLUS, PRECIOS_ANUNCIADOS_CLP, precioClp } from './productosPlus';
import { MAX_PROPIOS_GRATIS, MAX_PROPIOS_EXTENDIDA } from './procedimientosPropios';
import { FARMACOS_GRATIS, ESCALAS_GRATIS } from './accesoEnciclopedia';
import { casaDe } from './mundos';
import { PROCEDIMIENTOS } from '../data/referencia/procedimientos';

// Las fichas de cuidados del paciente de la Enciclopedia (grupo `cuidados`).
const FICHAS_CUIDADOS = PROCEDIMIENTOS.filter((p) => p.grupo === 'cuidados').length;

// El 25 de octubre de 2026, 00:00 de Chile: el mismo instante en que termina
// la transición de las estudiantes (Términos §6). Una sola fuente.
export const INICIO_MEMBRESIAS = ESTUDIANTE_FIN_TRANSICION;

export function antesDelInicio(ahora = Date.now()) {
  return ahora < Date.parse(INICIO_MEMBRESIAS);
}

// Lo que la cuenta gratuita tendrá desde el 25, sin mirar la transición ni el
// acceso abierto (que son de hoy): es lo que el aviso anuncia.
function cuotaGratisDesdeInicio(rol) {
  return { ...CUOTA_FREE, ...(CUOTA_POR_ROL[rol] || {}) };
}

// ── Quién es ─────────────────────────────────────────────────────────────────

/** 'estudiante' | 'enfermero' | 'tens' | 'sinRol', siempre del perfil REAL. */
export function segmentoInvitacion(profile) {
  return casaDe(profile) || 'sinRol';
}

/**
 * ¿Usa las dos experiencias? Full se presenta solo a ellas (Sebastián,
 * 20-oct). Dos señales que existen hoy: la estudiante en su internado (usa
 * MiPAE en turno además del PAE) y quien está mirando la otra experiencia en
 * este momento. Una enfermera que nunca abrió MiPAE Estudiante no ve Full.
 */
export function usaAmbasExperiencias(profile, mundo = null) {
  const casa = casaDe(profile);
  if (casa !== 'estudiante' && casa !== 'enfermero') return false;
  if (casa === 'estudiante' && profile?.etapa_internado === 'internado_en_curso') return true;
  return (mundo === 'estudiante' || mundo === 'enfermero') && mundo !== casa;
}

// ── Lo que ya tiene ─────────────────────────────────────────────────────────

/**
 * El acceso de hoy, en el vocabulario del aviso:
 *   membresia   pagada vigente (con producto o un permiso antiguo sin él)
 *   prueba      prueba en curso
 *   gratis      cuenta gratuita (con o sin prueba usada)
 */
export function accesoActual(profile, ahora = Date.now()) {
  if (profile?.plan === 'premium' && (!profile.premium_until || Date.parse(profile.premium_until) > ahora)) {
    const producto = PRODUCTOS_PLUS[profile.premium_product] ? profile.premium_product : null;
    return { tipo: 'membresia', producto, nombre: producto ? PRODUCTOS_PLUS[producto].nombre : null };
  }
  if (enTrial(profile, ahora)) {
    const producto = PRODUCTOS_PLUS[profile.trial_product] ? profile.trial_product : null;
    return { tipo: 'prueba', producto, nombre: producto ? PRODUCTOS_PLUS[producto].nombre : null, vence: new Date(finTrial(profile)).toISOString() };
  }
  return { tipo: 'gratis', producto: null, nombre: null };
}

const PRODUCTO_DE_CASA = { estudiante: 'plus_estudiante', enfermero: 'plus_enfermero' };

/**
 * La prueba de la experiencia de la persona:
 *   disponible    se puede activar y suma algo hoy: va el botón explícito
 *   desde_inicio  se puede activar, pero hoy el acceso abierto (o la
 *                 transición de la estudiante) ya le da lo mismo: se dice
 *                 desde cuándo, sin botón
 *   en_curso      ya la está usando
 *   usada         ya la usó; no se vuelve a ofrecer
 *   no_aplica     TENS, sin rol o con membresía pagada
 */
export function pruebaPara(profile, ahora = Date.now()) {
  const segmento = segmentoInvitacion(profile);
  const producto = PRODUCTO_DE_CASA[segmento] || null;
  const dias = diasTrialPara(profile);
  const base = {
    producto,
    nombre: producto ? PRODUCTOS_PLUS[producto].nombre : null,
    dias,
    boton: dias ? `Probar ${dias} días gratis` : null,
  };
  const acceso = accesoActual(profile, ahora);
  // El botón existe solo mientras la prueba se puede activar.
  const sinBoton = { ...base, boton: null };
  if (!producto || acceso.tipo === 'membresia') return { ...sinBoton, estado: 'no_aplica' };
  // En curso se nombra el producto con que se inició (cambiar de rol no lo cambia).
  if (acceso.tipo === 'prueba') return { ...sinBoton, nombre: acceso.nombre || base.nombre, estado: 'en_curso', vence: acceso.vence };
  if (profile?.trial_started_at) return { ...sinBoton, estado: 'usada' };
  if (!trialDisponible(profile)) return { ...sinBoton, estado: 'no_aplica' };
  return { ...base, estado: tieneAccesoPlus(profile, ahora) ? 'desde_inicio' : 'disponible' };
}

// "el 27 de octubre": la fecha en que termina una prueba, en hora de Chile.
export function fechaCorta(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', day: 'numeric', month: 'long' }).format(d);
  } catch {
    return null;
  }
}

// "una evolución completa con IA" o "2 evoluciones completas con IA", según
// la cuota gratuita de la semana (CUOTA_EVOLUCION_SEMANA).
function textoEvolucionesGratis({ completa = true } = {}) {
  if (CUOTA_EVOLUCION_SEMANA === 1) return `una evolución${completa ? ' completa' : ''} con IA`;
  return `${CUOTA_EVOLUCION_SEMANA} evoluciones${completa ? ' completas' : ''} con IA`;
}
const mayuscula = (t) => t.charAt(0).toUpperCase() + t.slice(1);

// ── Lo que se puede hacer gratis, por experiencia ───────────────────────────
// Cada fila es una herramienta con su utilidad, y `gratis` dice su límite en
// la cuenta gratuita desde el 25 (o "Incluido" si no tiene). Así se lee qué
// es gratis antes de leer qué suma la membresía.

function filasEstudiante() {
  const c = cuotaGratisDesdeInicio('estudiante');
  return [
    { id: 'valorar', label: 'Valorar con el modelo de tu ramo', detalle: 'Henderson, Gordon, Roper, Orem o Astorquiza. Marcas lo alterado y anotas el hallazgo.', gratis: 'Incluido' },
    { id: 'pae', label: 'Armar tu PAE con su fundamento', detalle: 'Diagnósticos NANDA-I con lo que los manifiesta, con qué se relacionan y el razonamiento clínico. Tú eliges y revisas.', gratis: `${c.paes} PAE al mes, compartido entre Rápido, Detallado y con tu pauta` },
    { id: 'benito', label: 'Preguntarle a Benito', detalle: 'Te explica un concepto, un modelo o por qué un cuidado se relaciona con tu caso.', gratis: `${c.benitoDia} consultas al día` },
    { id: 'repasar', label: 'Repasar a tu ritmo', detalle: 'El reto del día, el modo estudio, las escalas y las calculadoras.', gratis: 'Incluido' },
  ];
}

function filasEnfermero() {
  const c = cuotaGratisDesdeInicio('enfermero');
  return [
    { id: 'dictar', label: 'Dictar o escribir mientras pasa', detalle: 'Cada nota queda en la cronología del paciente con su hora. El examen, la piel y los dispositivos se registran con opciones simples.', gratis: 'Incluido, sin límite' },
    { id: 'evolucion', label: 'Preparar la evolución por sistemas', detalle: 'MiPAE ordena lo que registraste por sistema. Tú la revisas, la corriges y la copias a la ficha.', gratis: `${mayuscula(textoEvolucionesGratis())} por semana. La guía paso a paso y el registro manual, sin límite` },
    { id: 'benito', label: 'Benito en tu turno', detalle: 'Sabe en qué momento del turno estás y tus pendientes, sin datos que identifiquen a tus pacientes.', gratis: `${c.benitoDia} consultas al día` },
    { id: 'entregar', label: 'Entregar el turno ordenado', detalle: 'Calendario de turnos, examen físico, piel, escalas y la entrega de cada paciente.', gratis: 'Incluido' },
  ];
}

function filasTens() {
  return [
    { id: 'visita', label: 'Tu visita, punto por punto', detalle: 'Identificación, riesgos, dispositivos, alimentación y piel. Con eso se escribe tu evolución del turno.', gratis: 'Incluido' },
    { id: 'registrar', label: 'Registrar de un toque', detalle: 'Desde la lista de pacientes ves lo que viene y lo registras sin entrar a la ficha.', gratis: 'Incluido' },
    { id: 'entregar', label: 'Tu evolución y tu entrega', detalle: 'Revisas el texto antes de copiarlo. Lo que no alcanzaste a avisar se entrega como "por informar".', gratis: 'Incluido' },
    { id: 'cuidados', label: 'Cuidados del paciente', detalle: `${FICHAS_CUIDADOS} fichas en la Enciclopedia, con su porqué y cuándo avisar.`, gratis: 'Incluido' },
  ];
}

function filasSinRol() {
  return [
    { id: 'estudiante', label: 'MiPAE Estudiante', detalle: 'Valorar con el modelo de tu ramo, armar tu PAE con su fundamento y repasar a tu ritmo.', gratis: 'Con cuenta gratuita' },
    { id: 'enfermero', label: 'MiPAE Enfermero', detalle: 'Registrar el turno por voz o paso a paso y preparar la evolución por sistemas.', gratis: 'Con cuenta gratuita' },
    { id: 'tens', label: 'MiPAE TENS', detalle: 'Registrar los cuidados de tu turno y entregarlos ordenados.', gratis: 'Gratuito' },
  ];
}

// ── Lo que suma cada Plus (y solo eso) ──────────────────────────────────────
// Mismas palabras que /precios y Planes. `soloWeb` marca lo que se ofrece en
// la web (el PDF de Benito se descarga ahí): la app instalada no lo nombra.

const BENITO_PLUS = `Hasta ${CUOTA_PLUS.benitoDia} consultas al día a Benito, con un máximo de ${CUOTA_PLUS.benitoMes} al mes`;
const ENCICLOPEDIA_PLUS = 'La Enciclopedia completa y la explicación de laboratorio con IA';

const SUMA = {
  plus_estudiante: [
    { id: 'pae', label: 'PAE sin cuota mensual', detalle: 'Rápido, Detallado y con tu pauta.' },
    { id: 'benito', label: BENITO_PLUS },
    { id: 'ramos', label: 'Benito recuerda tus ramos' },
    { id: 'enciclopedia', label: ENCICLOPEDIA_PLUS, detalle: `Todos los fármacos y escalas, y hasta ${MAX_PROPIOS_EXTENDIDA} procedimientos propios.` },
    { id: 'pdf', label: 'Las respuestas de Benito en PDF, en la web', soloWeb: true },
  ],
  plus_enfermero: [
    { id: 'evolucion', label: `Hasta ${CUOTA_EVOLUCION_SEMANA_PLUS} evoluciones completas con IA por semana`, detalle: 'Una evolución es un paciente en un turno; corregirla no descuenta otra.' },
    { id: 'benito', label: BENITO_PLUS },
    { id: 'enciclopedia', label: ENCICLOPEDIA_PLUS, detalle: `Todos los fármacos y escalas, y hasta ${MAX_PROPIOS_EXTENDIDA} procedimientos propios.` },
    { id: 'pdf', label: 'Las respuestas de Benito en PDF, en la web', soloWeb: true },
  ],
};

export function sumaMembresia(producto, { nativo = false } = {}) {
  return (SUMA[producto] || []).filter((f) => !(nativo && f.soloWeb));
}

// La línea de la Enciclopedia gratuita, con los mismos catálogos que la aplican.
export function enciclopediaGratis() {
  return `${FARMACOS_GRATIS.length} medicamentos, ${ESCALAS_GRATIS.length} escalas, los valores de laboratorio y hasta ${MAX_PROPIOS_GRATIS} procedimientos propios`;
}

// ── El contenido por experiencia ─────────────────────────────────────────────

const CONTENIDO = {
  estudiante: {
    vineta: 'pae',
    eyebrow: 'MiPAE Estudiante',
    titulo: 'Del caso a tu PAE, con el fundamento de cada cuidado',
    bajada: 'Organiza la valoración con el modelo de tu ramo, elige diagnósticos con su razonamiento y repasa a tu ritmo.',
    filas: filasEstudiante,
  },
  enfermero: {
    vineta: 'evolucion',
    eyebrow: 'MiPAE Enfermero',
    titulo: 'Dicta tu turno y revisa la evolución antes de copiarla',
    bajada: 'MiPAE en turno ordena por sistemas lo que vas registrando. Tú revisas la evolución, la corriges y la copias a la ficha.',
    filas: filasEnfermero,
  },
  tens: {
    vineta: 'tens',
    eyebrow: 'MiPAE TENS',
    titulo: 'Registra los cuidados de tu turno de un toque',
    bajada: 'Ves qué toca con cada paciente, lo registras desde la lista y entregas tu turno con lo importante a la vista.',
    filas: filasTens,
  },
  sinRol: {
    vineta: null,
    eyebrow: 'MiPAE',
    titulo: 'Elige tu experiencia y MiPAE se ordena para ti',
    bajada: 'Es una sola app con tres experiencias. Cuéntanos tu rol en Mi perfil y el inicio te muestra tus herramientas.',
    filas: filasSinRol,
  },
};

/**
 * Todo lo que el aviso y la pantalla necesitan decir a esta persona.
 *
 * opciones:
 *   ahora        reloj (tests)
 *   nativo       app instalada: sin precio ni suscripción
 *   mundo        la experiencia que está mirando (para Full)
 *   contratable  (producto) => boolean, la sonda del servidor. Sin ella, o en
 *                nativo, no se invita a suscribirse.
 */
export function invitacionPara(profile, { ahora = Date.now(), nativo = false, mundo = null, contratable = null } = {}) {
  const segmento = segmentoInvitacion(profile);
  const c = CONTENIDO[segmento];
  const antes = antesDelInicio(ahora);
  const acceso = accesoActual(profile, ahora);
  const disponible = pruebaPara(profile, ahora);
  const prueba = nativo && ['disponible','desde_inicio'].includes(disponible.estado) ? { ...disponible, estado: 'no_disponible' } : disponible;
  const producto = PRODUCTO_DE_CASA[segmento] || null;

  // 1. Qué puedes hacer, con la nota de lo que hoy sigue igual.
  let notaHoy = null;
  if (segmento === 'enfermero' && antes) notaHoy = 'Hasta el 24 de octubre MiPAE Enfermero sigue abierto sin estos límites. Rigen desde el 25.';
  if (segmento === 'estudiante' && enTransicionEstudiante(profile, ahora)) notaHoy = 'Hasta el 24 de octubre conservas las condiciones con que creaste tu cuenta.';
  if (segmento === 'tens') notaHoy = 'MiPAE TENS es gratuito en esta etapa y no necesita membresía.';

  // 2. Qué te aporta la membresía. Con una membresía de la experiencia, lo
  // que ya incluye (sin vender); sin ella, lo que sumaría.
  // En la app instalada, la membresía se nombra solo si la persona la tiene o
  // la puede probar ahí mismo (el recorrido permitido de Planes nativo): una
  // vitrina sin prueba sería un llamado a comprar afuera (venta-solo-web,
  // guía 3.1.3(f) de Apple).
  const pruebaEnJuego = ['disponible', 'desde_inicio', 'en_curso'].includes(prueba.estado);
  let membresia = null;
  if (producto && (!nativo || pruebaEnJuego || acceso.tipo === 'membresia')) {
    const nombre = PRODUCTOS_PLUS[producto].nombre;
    const tiene = (acceso.tipo === 'membresia' && (!acceso.producto || acceso.producto === producto || acceso.producto === 'full'))
      || (acceso.tipo === 'prueba' && (!acceso.producto || acceso.producto === producto));
    membresia = {
      producto,
      nombre: acceso.tipo === 'membresia' && acceso.producto === 'full' ? PRODUCTOS_PLUS.full.nombre : nombre,
      titulo: tiene ? `Lo que ya incluye ${acceso.tipo === 'membresia' && acceso.producto === 'full' ? 'Full' : nombre}` : `Lo que suma ${nombre}`,
      filas: sumaMembresia(producto, { nativo }),
      tiene,
    };
  }

  // Full, solo a quien usa las dos experiencias y no la tiene ya. En la app
  // instalada no se presenta: no tiene prueba propia, así que ahí sería una
  // vitrina de algo que solo se contrata afuera (venta-solo-web).
  const full = !nativo && usaAmbasExperiencias(profile, mundo) && !(acceso.tipo === 'membresia' && acceso.producto === 'full')
    ? {
      nombre: PRODUCTOS_PLUS.full.nombre,
      texto: segmento === 'estudiante' && profile?.etapa_internado === 'internado_en_curso'
        ? 'En tu internado usas las dos experiencias. Full reúne Estudiante Plus y Enfermero Plus, con PAE sin cuota y más evoluciones con IA en MiPAE en turno.'
        : 'Si usas MiPAE Estudiante y MiPAE Enfermero, Full reúne Estudiante Plus y Enfermero Plus en una sola membresía.',
      precio: nativo ? null : `${precioClp(PRECIOS_ANUNCIADOS_CLP.full)} al mes`,
    }
    : null;

  // 3. La invitación.
  const invitacion = { prueba: null, aviso: null, suscribir: null };
  if (prueba.estado === 'disponible') {
    invitacion.prueba = {
      boton: prueba.boton,
      condiciones: condicionesPruebaTarjeta(producto),
    };
  } else if (prueba.estado === 'desde_inicio') {
    invitacion.aviso = antes
      ? `Desde el 25 de octubre puedes probar ${prueba.nombre} ${prueba.dias} días gratis con tarjeta y cobro automático al terminar si no cancelas antes.`
      : `Cuando termine el acceso abierto de octubre podrás probar ${prueba.nombre} ${prueba.dias} días gratis con tarjeta y cobro automático al terminar si no cancelas antes.`;
  } else if (prueba.estado === 'en_curso') {
    const fecha = fechaCorta(prueba.vence);
    invitacion.aviso = profile?.trial_con_tarjeta
      ? `Tu prueba de ${prueba.nombre} sigue${fecha ? ` hasta el ${fecha}` : ''}. Revisa el estado de tu acceso en Mi perfil.`
      : fecha
      ? `Tu prueba de ${prueba.nombre} sigue hasta el ${fecha}. Después vuelves a tu cuenta gratuita y lo que creaste sigue guardado.`
      : `Estás usando tu prueba de ${prueba.nombre}. Después vuelves a tu cuenta gratuita y lo que creaste sigue guardado.`;
  } else if (acceso.tipo === 'membresia') {
    invitacion.aviso = acceso.nombre
      ? `Tienes ${acceso.nombre}. Sus condiciones y su vigencia se mantienen.`
      : 'Tu acceso ampliado se mantiene con las condiciones con que lo recibiste.';
  }
  // Suscribirse: solo en la web, solo con la contratación habilitada por el
  // servidor, y nunca a quien ya tiene una membresía pagada.
  if (!nativo && producto && acceso.tipo !== 'membresia' && typeof contratable === 'function' && contratable(producto)) {
    invitacion.suscribir = {
      boton: `Suscribirme a ${PRODUCTOS_PLUS[producto].nombre}`,
      precio: `${precioClp(PRECIOS_ANUNCIADOS_CLP[producto])} al mes`,
      producto,
    };
  }

  // 4. Las condiciones, cortas y con su acceso a lo completo.
  const condiciones = [];
  if (producto && !nativo) {
    const nombre = PRODUCTOS_PLUS[producto].nombre;
    const valor = `${precioClp(PRECIOS_ANUNCIADOS_CLP[producto])} al mes, IVA incluido`;
    condiciones.push(antes
      ? `${nombre} costará ${valor}, y se podrá contratar desde el 25 de octubre. Se renueva cada mes y cancelas la renovación cuando quieras desde Mi perfil en la web.`
      : `${nombre} cuesta ${valor}. Se renueva cada mes y cancelas la renovación cuando quieras desde Mi perfil en la web.`);
  }
  if (!nativo && (prueba.estado === 'disponible' || prueba.estado === 'desde_inicio')) condiciones.push(condicionesPruebaTarjeta(producto));
  if (producto) {
    const cg = cuotaGratisDesdeInicio(segmento);
    condiciones.push(segmento === 'estudiante'
      ? `La cuenta gratuita sigue: ${cg.paes} PAE al mes, ${cg.benitoDia} consultas diarias a Benito y la Enciclopedia con ${enciclopediaGratis()}. Lo que creaste sigue disponible.`
      : `La cuenta gratuita sigue: ${textoEvolucionesGratis()} por semana, ${cg.benitoDia} consultas diarias a Benito, ${cg.revisas} usos de Revisa al mes y la Enciclopedia con ${enciclopediaGratis()}. Lo que creaste sigue disponible.`);
  }

  return {
    segmento,
    antesDelInicio: antes,
    eyebrow: c.eyebrow,
    titulo: c.titulo,
    bajada: c.bajada,
    vineta: c.vineta,
    puedes: c.filas(),
    notaHoy,
    acceso,
    prueba,
    membresia,
    full,
    invitacion,
    condiciones,
  };
}

// ── El aviso del inicio ──────────────────────────────────────────────────────
// Antes del 25 su acción es conocer (Sebastián: "prioriza Conoce lo nuevo o
// Descubre qué incluye Plus"). Desde el 25, si la prueba suma algo, el aviso
// puede ofrecerla con su botón explícito. Abrir el aviso nunca la inicia.
export function accionAviso(profile, { ahora = Date.now(), nativo = false } = {}) {
  const segmento = segmentoInvitacion(profile);
  if (segmento === 'sinRol') return { id: 'perfil', texto: 'Elegir mi experiencia' };
  const disponible = pruebaPara(profile, ahora);
  const prueba = nativo && ['disponible','desde_inicio'].includes(disponible.estado) ? { ...disponible, estado: 'no_disponible' } : disponible;
  if (!antesDelInicio(ahora) && prueba.estado === 'disponible') {
    return { id: 'prueba', texto: prueba.boton, secundaria: { id: 'descubre', texto: 'Conoce lo nuevo' } };
  }
  // En la app instalada, "Plus" se nombra solo si la prueba está en juego:
  // sin ella, la pantalla no muestra la membresía y el botón prometería algo
  // que no está ahí.
  const plusALaVista = !nativo;
  if ((segmento === 'estudiante' || segmento === 'enfermero') && accesoActual(profile, ahora).tipo === 'gratis' && plusALaVista) {
    return { id: 'descubre', texto: 'Descubre qué incluye Plus' };
  }
  return { id: 'descubre', texto: 'Conoce lo nuevo' };
}

// La línea del aviso bajo la muestra: lo gratis y lo que suma, en una frase
// (el aviso no es una tabla de condiciones). Con una membresía o una prueba
// en curso, lo que ya tiene.
export function resumenAviso(profile, { ahora = Date.now(), nativo = false } = {}) {
  const segmento = segmentoInvitacion(profile);
  const acceso = accesoActual(profile, ahora);
  const prueba = pruebaPara(profile, ahora);
  if (segmento === 'tens') return 'MiPAE TENS es gratuito en esta etapa y no necesita membresía.';
  if (segmento === 'sinRol') return 'Cuéntanos tu rol y el inicio te muestra tus herramientas.';
  if (prueba.estado === 'en_curso') {
    const fecha = fechaCorta(prueba.vence);
    return fecha ? `Tu prueba de ${prueba.nombre} sigue hasta el ${fecha}.` : `Estás usando tu prueba de ${prueba.nombre}.`;
  }
  if (acceso.tipo === 'membresia') return acceso.nombre ? `Tienes ${acceso.nombre}.` : 'Tu acceso ampliado se mantiene.';
  const c = cuotaGratisDesdeInicio(segmento);
  const desde = antesDelInicio(ahora) ? 'Desde el 25, tu' : 'Tu';
  // En la app instalada, sin prueba que ofrecer no se nombra la membresía.
  const nombraPlus = !nativo || prueba.estado === 'disponible' || prueba.estado === 'desde_inicio';
  if (segmento === 'estudiante') {
    return `${desde} cuenta gratuita incluye ${c.paes} PAE al mes.${nombraPlus ? ' Estudiante Plus lo deja sin cuota mensual.' : ''}`;
  }
  return `${desde} cuenta gratuita incluye ${textoEvolucionesGratis({ completa: false })} por semana.${nombraPlus ? ` Enfermero Plus suma hasta ${CUOTA_EVOLUCION_SEMANA_PLUS}.` : ''}`;
}

